import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

type Theme = 'light' | 'dark'

export const useUIStore = defineStore('ui', () => {
  const sidebarCollapsed = ref<boolean>(false)
  const theme = ref<Theme>('light')

  // Eş zamanlı yükleme kaynaklarını (navigasyon + API çağrıları) say.
  // Sayaç > 0 olduğu sürece global yükleme çubuğu görünür.
  const loadingCount = ref<number>(0)
  const globalLoading = computed<boolean>(() => loadingCount.value > 0)

  function startLoading() {
    loadingCount.value++
  }

  function stopLoading() {
    if (loadingCount.value > 0) loadingCount.value--
  }

  const notifications = ref<Array<{ id: string; type: string; message: string; duration?: number }>>([])

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed.value))
  }

  function setTheme(newTheme: Theme) {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)

    const html = document.documentElement
    if (newTheme === 'dark') {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }
  }

  function toggleTheme() {
    setTheme(theme.value === 'light' ? 'dark' : 'light')
  }

  function initializeTheme() {
    const stored = localStorage.getItem('theme') as Theme | null
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const initialTheme: Theme = stored || (prefersDark ? 'dark' : 'light')

    setTheme(initialTheme)
  }

  function initializeSidebar() {
    const stored = localStorage.getItem('sidebarCollapsed')
    if (stored !== null) {
      sidebarCollapsed.value = stored === 'true'
    }
  }

  function addNotification(type: string, message: string, duration?: number) {
    const id = Math.random().toString(36).substr(2, 9)
    notifications.value.push({ id, type, message, duration })

    if (duration) {
      setTimeout(() => {
        notifications.value = notifications.value.filter((n) => n.id !== id)
      }, duration)
    }

    return id
  }

  function removeNotification(id: string) {
    notifications.value = notifications.value.filter((n) => n.id !== id)
  }

  return {
    sidebarCollapsed,
    theme,
    globalLoading,
    startLoading,
    stopLoading,
    notifications,
    toggleSidebar,
    setTheme,
    toggleTheme,
    initializeTheme,
    initializeSidebar,
    addNotification,
    removeNotification
  }
})
